import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

export default function CompanyFormModal({ company, onSave, onClose }) {
  const isEdit = !!company;
  const [form, setForm] = useState({
    nome_fantasia: '',
    razao_social: '',
    cnpj: '',
    observacoes_internas: '',
    status: 'ativo',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (company) {
      setForm({
        nome_fantasia: company.nome_fantasia || '',
        razao_social: company.razao_social || '',
        cnpj: company.cnpj || '',
        observacoes_internas: company.observacoes_internas || '',
        status: company.status || 'ativo',
      });
    } else {
      setForm({
        nome_fantasia: '',
        razao_social: '',
        cnpj: '',
        observacoes_internas: '',
        status: 'ativo',
      });
    }
  }, [company]);

  const validate = () => {
    const e = {};
    if (!form.nome_fantasia?.trim()) {
      e.nome_fantasia = 'Informe o nome fantasia';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      nome_fantasia: form.nome_fantasia.trim(),
      razao_social: form.razao_social?.trim() || null,
      cnpj: form.cnpj?.trim() || null,
      observacoes_internas: form.observacoes_internas?.trim() || '',
      status: form.status || 'ativo',
    };

    const dataToSave = isEdit
      ? { ...company, ...payload }
      : { ...payload };

    setSaving(true);
    try {
      await onSave?.(dataToSave);
      onClose();
    } catch (_) {
      // Erro já exibido via toast no App
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-appSurface rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-appSurfaceBorder">
          <h2 className="text-xl font-bold text-appText">
            {isEdit ? 'Editar empresa' : 'Nova empresa'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-appTextMuted hover:bg-appSurfaceBorder/50 rounded-lg"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">
              Nome fantasia *
            </label>
            <input
              type="text"
              value={form.nome_fantasia}
              onChange={(e) => setForm((f) => ({ ...f, nome_fantasia: e.target.value }))}
              placeholder="Ex: Tech Solutions"
              className={`w-full px-4 py-3 rounded-xl border text-appText focus:ring-2 focus:ring-appPrimary outline-none ${
                errors.nome_fantasia ? 'border-rose-500' : 'border-appSurfaceBorder'
              }`}
            />
            {errors.nome_fantasia && (
              <p className="text-xs text-rose-600 mt-1">{errors.nome_fantasia}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">
              Razão social (opcional)
            </label>
            <input
              type="text"
              value={form.razao_social}
              onChange={(e) => setForm((f) => ({ ...f, razao_social: e.target.value }))}
              placeholder="Ex: Tech Solutions Ltda"
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">
              CNPJ (opcional)
            </label>
            <input
              type="text"
              value={form.cnpj}
              onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value }))}
              placeholder="00.000.000/0001-00"
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">
              Observações internas (opcional)
            </label>
            <textarea
              value={form.observacoes_internas}
              onChange={(e) => setForm((f) => ({ ...f, observacoes_internas: e.target.value }))}
              placeholder="Anotações sobre a empresa..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none resize-none"
            />
          </div>

          {isEdit && (
            <div>
              <label className="text-xs font-bold text-appTextMuted uppercase block mb-1">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-appSurfaceBorder text-appText focus:ring-2 focus:ring-appPrimary outline-none bg-white"
              >
                <option value="ativo">Ativo</option>
                <option value="arquivado">Arquivado</option>
              </select>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl font-bold text-sm border border-appSurfaceBorder text-appText hover:bg-appSurfaceBorder/30"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 rounded-xl font-bold text-sm bg-appPrimary text-white hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving ? 'Salvando...' : (isEdit ? 'Salvar' : 'Criar empresa')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
